import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, ArrowRight, Eye, EyeOff, Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_DARK_BG } from "@/lib/brand";

/**
 * Dedicated admin sign-in at `/admin`. Not linked from the public site — admins
 * bookmark it. Buyer/Partner/Builder logins live at `/login`.
 */
export default function AdminLogin() {
    const nav = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [pw, setPw]       = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy]   = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const res = await login(email.trim(), pw);
        setBusy(false);
        if (!res.ok) { toast.error(res.error); return; }
        if (res.user.role !== "admin") {
            toast.error("This portal is for administrators only.");
            return;
        }
        toast.success(`Welcome back, ${res.user.name}`);
        nav("/admin/dashboard");
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0a0d18] px-5 py-12 relative overflow-hidden" data-testid="admin-login-page">
            {/* Ambient gold glow */}
            <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-[0.08]"
                 style={{ background: "radial-gradient(circle, var(--ebg-gold) 0%, transparent 60%)" }}/>
            <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
                 style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "80px 80px" }}/>

            <div className="relative w-full max-w-md">
                <Link to="/" className="block text-center mb-8">
                    <img src={LOGO_DARK_BG} alt="EBG Realty" className="h-12 w-auto mx-auto opacity-95"/>
                </Link>

                <div className="bg-[#111624] border border-white/8 shadow-[0_25px_80px_-20px_rgba(0,0,0,0.6)] p-8 md:p-10">
                    <div className="w-12 h-12 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 flex items-center justify-center mb-6">
                        <Shield className="text-[var(--ebg-gold)]" size={20}/>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Administrator Access</div>
                    <h1 className="font-serif text-3xl md:text-4xl mt-3 leading-tight text-white">Sign in to your<br/>admin console.</h1>
                    <p className="text-white/50 mt-4 text-[13px]">Restricted access — this portal is for EBG Realty administrators only.</p>

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">Email</div>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" required
                                autoComplete="username"
                                className="w-full bg-[#0a0d18] border border-white/12 text-white px-4 py-3 text-[14px] focus:border-[var(--ebg-gold)] focus:outline-none placeholder:text-white/25"
                                placeholder="admin@ebgrealty.com"/>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">Password</div>
                            <div className="relative">
                                <input type={showPw ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-login-password" required
                                    autoComplete="current-password"
                                    className="w-full bg-[#0a0d18] border border-white/12 text-white px-4 py-3 pr-11 text-[14px] focus:border-[var(--ebg-gold)] focus:outline-none"/>
                                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white" aria-label={showPw ? "Hide password" : "Show password"}>
                                    {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                                </button>
                            </div>
                        </div>

                        <button disabled={busy} type="submit" data-testid="admin-login-submit"
                            className="w-full bg-[var(--ebg-gold)] text-black py-3 text-[11px] uppercase tracking-[0.28em] font-bold disabled:opacity-60 hover:bg-white hover:text-black transition-colors inline-flex items-center justify-center gap-2">
                            {busy ? <><Loader2 size={13} className="animate-spin"/> Signing in…</> : <>Sign In <ArrowRight size={13}/></>}
                        </button>
                    </form>

                    <div className="mt-6 pt-5 border-t border-white/8 text-[11px] text-white/40 flex items-center gap-2">
                        <Lock size={11}/> Encrypted end-to-end · Restricted access · All sessions audited
                    </div>
                </div>

                <div className="mt-6 text-center text-[11px] text-white/40">
                    Not an admin? <Link to="/login" className="text-[var(--ebg-gold)] hover:text-white">Sign in as buyer, partner or builder</Link>
                </div>
            </div>
        </div>
    );
}
